/**
 * Faction context. Lets the player choose one of four faction shields from the
 * Home page. The chosen faction sets a global color theme (gold / red / green /
 * blue) that flows through the medieval CSS variables — buttons, banners,
 * headings, panel accents — without touching individual components.
 *
 * Selection persists in localStorage under `chess_faction` and is applied as
 * a `data-faction` attribute on <html>. The matching `[data-faction="..."]`
 * blocks live in App.css and rebind the existing `--gilt`, `--gilt-light`,
 * `--royal`, `--royal-deep` tokens plus a faction `--theme-glow` used for
 * shield selection states.
 */
import { createContext, useContext, useEffect, useState } from "react";

export const FACTIONS = [
  {
    value: "iron-kingdom",
    label: "Iron Kingdom",
    tagline: "Forged in fire, crowned in gold.",
    image: "https://customer-assets.emergentagent.com/job_crown-conquest-9/artifacts/bq7vljqr_1000001320.png",
    glow: "rgba(201, 167, 75, 0.65)",
  },
  {
    value: "dragonborn",
    label: "Dragonborn",
    tagline: "Scaled in flame, crowned in ruby.",
    image: "https://customer-assets.emergentagent.com/job_crown-conquest-9/artifacts/0ibmvypy_1000001321.png",
    glow: "rgba(196, 65, 58, 0.7)",
  },
  {
    value: "elf-court",
    label: "Elf Court",
    tagline: "Rooted in moonlight, crowned in emerald.",
    image: "https://customer-assets.emergentagent.com/job_crown-conquest-9/artifacts/fdkdo3z7_1000001319.png",
    glow: "rgba(79, 160, 88, 0.65)",
  },
  {
    value: "celestial-host",
    label: "Celestial Host",
    tagline: "Born of starlight, crowned in sapphire.",
    image: "https://customer-assets.emergentagent.com/job_crown-conquest-9/artifacts/bde4wqvi_1000001322.png",
    glow: "rgba(75, 123, 201, 0.7)",
  },
];

const DEFAULT_FACTION = "iron-kingdom";
const STORAGE_KEY = "chess_faction";

// Snapshot at module load — captures whether localStorage held a pledge
// BEFORE the FactionProvider's useEffect writes the default back. Lets
// us distinguish "user actually pledged at some point" from "just the
// default that was auto-persisted on first render".
const INITIAL_PLEDGE = typeof window !== "undefined"
  ? window.localStorage.getItem(STORAGE_KEY)
  : null;

const Ctx = createContext({
  faction: DEFAULT_FACTION,
  setFaction: () => {},
  hasPledged: false,
  factionMeta: FACTIONS[0],
});

export function FactionProvider({ children }) {
  const [faction, setFactionState] = useState(() => {
    if (typeof window === "undefined") return DEFAULT_FACTION;
    const saved = window.localStorage.getItem(STORAGE_KEY);
    return FACTIONS.some((f) => f.value === saved) ? saved : DEFAULT_FACTION;
  });
  const [hasPledged, setHasPledged] = useState(!!INITIAL_PLEDGE);

  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.setAttribute("data-faction", faction);
    }
    if (typeof window !== "undefined") {
      window.localStorage.setItem(STORAGE_KEY, faction);
    }
  }, [faction]);

  // Wrapper: any explicit setFaction call (from Home shield tap, Profile
  // dropdown, Settings dropdown) counts as an actual pledge — flip the
  // flag so pieces / wordmark / tinted UI treat this session as pledged
  // without waiting for a page reload.
  const setFaction = (value) => {
    setFactionState(value);
    setHasPledged(true);
  };

  const factionMeta = FACTIONS.find((f) => f.value === faction) || FACTIONS[0];

  return (
    <Ctx.Provider value={{ faction, setFaction, hasPledged, factionMeta }}>
      {children}
    </Ctx.Provider>
  );
}

export function useFaction() {
  return useContext(Ctx);
}
